/**
 * Wire shapes for /api/v1/uviva endpoints.
 * Mirrors Pydantic models in middleware/app/api/routes/uviva.py.
 */

export type SubmissionStatus =
  | 'received'
  | 'validating'
  | 'validated'
  | 'posting'
  | 'posted'
  | 'pending_review'
  | 'rejected'
  | 'post_failed'
  | 'validation_failed'

/** 2-bucket simplification of SubmissionStatus for the vendor's own view —
 *  see middleware/app/api/routes/uviva.py's _vendor_status(). Vendors never
 *  see internal-plumbing wording like "Post Failed", and critically never
 *  see "Rejected" — anything short of a successful post, including
 *  rejected, reads as "pending" (still being worked on our end, not
 *  something for Uviva to worry about). */
export type VendorStatus = 'pending' | 'posted'

/** 'spi_order' reconciles to an SPI PO (inbound receipt — wherever the PO's
 *  own Odoo picking lands, SPI's own warehouse or UVWH; see
 *  destination_warehouse_name on SubmissionSummary). 'uvwh_intake' is a
 *  PO-less inbound receipt into UVWH (Uviva restocking their own held
 *  consignment inventory). 'uviva_direct' is a Uviva service order SPI
 *  ships out (outbound OUT operation). */
export type OrderType = 'spi_order' | 'uvwh_intake' | 'uviva_direct'

export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  spi_order: 'SPI Order',
  uvwh_intake: 'UVWH Stock Intake',
  uviva_direct: 'Uviva Direct Order',
}

export interface ShipToContact {
  id: number
  name: string
  address: string | null
  city: string | null
  country: string | null
}

/** How a FAILED rule should be handled — drives which reviewer actions show.
 *  fix_in_odoo: correct routing/master data in Odoo, then re-run validation
 *  (Approve is blocked). vendor_data_error: reject so Uviva can correct.
 *  duplicate: mark as duplicate. spi_decision: approve or reject at SPI's call.
 *  missing_cofa: a lot has no matching CofA; never approvable, attach + re-run or reject. */
export type FailureKind = 'fix_in_odoo' | 'vendor_data_error' | 'duplicate' | 'spi_decision' | 'missing_cofa'

export interface ValidationResult {
  rule_code: string
  passed: boolean
  detail: string | null
  /** Plain-language rule name (e.g. "PO routes to UVWH consignment"). */
  label?: string | null
  /** Only present on failed rules. */
  kind?: FailureKind
  action?: string
}

/** Submission-level document types — bill_of_lading/invoice are attachable
 *  after create too; shipment_sheet is only ever attached at create time. */
export type DocType = 'shipment_sheet' | 'bill_of_lading' | 'invoice'

export interface SubmissionDocument {
  document_id: number
  doc_type?: DocType
  filename: string
  content_type: string | null
  size_bytes: number | null
  uploaded_by: string | null
  uploaded_at: string | null
}

export interface SubmissionLine {
  id: number
  product_code: string
  product_name: string | null
  lot_serial: string
  qty_to_ship: number
  uom: string | null
  country_of_origin: string | null
  sell_by: string | null
  coa_production_date: string | null
  coa_retest_date: string | null
  status: string
  odoo_lot_id: number | null
  odoo_lot_name: string | null
  odoo_move_line_id: number | null
  coa_documents: SubmissionDocument[]
  /** Omitted entirely for a uviva-role caller — internal PO-matching rule
   *  codes (V1-V9) they have no context for. */
  validation_results?: ValidationResult[]
}

export type RoutingMode = 'consignment' | 'standard'

export interface SubmissionSummary {
  id: string
  shipment_ref: string
  po_ref: string | null
  order_ref: string | null
  customer_code: string | null
  planned_delivery_date: string | null
  order_type: OrderType
  /** Physical warehouse the receipt actually lands in — derived from the
   *  PO's own Odoo picking for spi_order (never assumed), always "UVWH
   *  (Uviva Warehouse)" for uvwh_intake, null for uviva_direct (source, not
   *  destination, is UVWH there) or before validation resolves a PO. */
  destination_warehouse_name: string | null
  /** Reviewer-assigned receiving type for spi_order; null = auto-detect. */
  routing_mode: RoutingMode | null
  ship_to_partner_id: number | null
  ship_to_name: string | null
  status: SubmissionStatus
  vendor_status: VendorStatus
  submitted_by: string
  submitted_at: string
  resolved_by: string | null
  resolved_at: string | null
  resolution_note: string | null
  /** Genuinely shared — visible AND editable by both SPI staff and Uviva,
   *  independent of any status transition. Distinct from resolution_note
   *  (vendor-facing but only ever set by reject/close-as-duplicate) and
   *  internal_note (SPI-only). */
  notes: string | null
  odoo_picking_id: number | null
  odoo_picking_name: string | null
  odoo_picking_state: string | null
  tracking_number: string | null
  carrier_name: string | null
  delivered_at: string | null
  /** Omitted entirely for a uviva-role caller — raw internal/Odoo exception
   *  text. See middleware/app/api/routes/uviva.py's role-gated
   *  _submission_to_dict. */
  last_error?: string | null
  /** Omitted entirely for a uviva-role caller — ops' own approve-exception
   *  rationale, never meant for the vendor (resolution_note is the field
   *  that's genuinely vendor-facing, written by reject_submission()). */
  internal_note?: string | null
  created_at: string
  updated_at: string
  attached_documents: SubmissionDocument[]
  lines?: SubmissionLine[]
}

export interface SubmissionListResponse {
  total: number
  items: SubmissionSummary[]
}

export interface InventoryRow {
  product_code: string
  product_name: string
  lot_serial: string | null
  on_hand: number
  reserved: number
  available: number
  uom: string
  sell_by: string | null
  location: string
}

export interface InventoryResponse {
  rows: InventoryRow[]
  count: number
  cached_at: string | null
}

export interface POLine {
  product_code: string
  product_name: string
  qty_ordered: number
  qty_received: number
  qty_remaining: number
  uom: string
}

export interface POLinesResponse {
  po_ref: string
  po_id: number
  partner: string
  lines: POLine[]
}

export interface OrderHistoryRow {
  po_ref: string
  po_id: number
  date_order: string | null
  status: string
  lines: Array<{
    product_code: string
    product_name: string
    qty_ordered: number
    qty_received: number
    uom: string
  }>
}

/** Flattened view of OrderHistoryRow for grouped DataTable rendering. */
export interface OrderHistoryLine {
  po_ref: string
  date_order: string | null
  status: string
  product_code: string
  product_name: string
  qty_ordered: number
  qty_received: number
  uom: string
}

export interface OrderHistoryResponse {
  rows: OrderHistoryRow[]
  count: number
}

export interface DeliveryPerformanceRow {
  po_ref: string
  picking_name: string
  planned_date: string | null
  actual_date: string | null
  on_time: boolean | null
  days_variance: number | null
  qty_done: number
}

export interface DeliveryPerformanceResponse {
  rows: DeliveryPerformanceRow[]
  count: number
  on_time_rate: number | null
}

export interface CustomerDemandRow {
  product_code: string
  product_name: string
  line_type: 'commercial' | 'sample'
  customer_name: string
  so_name: string
  qty_ordered: number
  qty_delivered: number
  delivery_date: string | null
  status: string
}

export interface CustomerDemandResponse {
  rows: CustomerDemandRow[]
  count: number
}

export interface DashboardKPIs {
  open_po_count: number | null
  pending_review_count: number
  posted_this_month: number
  uvwh_item_count: number | null
  recent_submissions: SubmissionSummary[]
}

export interface ExceptionStats {
  pending_review: number
  post_failed: number
  validation_failed: number
  posted: number
  rejected: number
}

export interface SubmissionListFilters {
  status?: SubmissionStatus
  order_type?: OrderType
  po_ref?: string
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}
